import { MAX_SIDEBAR_CONTAINERS } from 'app-constants'
import { IResourcesUpdatedSocket } from 'types'

/**
 * The sidebar containers (folder ids, null = the hub's top level) whose direct children changed, or
 * null for "anything may have changed: refresh it all".
 */
export type SidebarContainers = (string | null)[] | null

/** How long after the first event of a burst to wait for more, so a burst costs one update. */
export const RESOURCES_UPDATED_WINDOW_MS = 300

/** What an event says changed. Anything but a list of ids and nulls means everything. */
export const containersOf = (event: IResourcesUpdatedSocket): SidebarContainers =>
  // An empty list names nothing to read back, so it means a full refresh, like no list at all.
  Array.isArray(event.containers) && event.containers.length > 0 ? event.containers : null

/** Both sets of containers, each once; everything if either is, or if together they are too many. */
export const mergeContainers = (a: SidebarContainers, b: SidebarContainers): SidebarContainers => {
  if (a === null || b === null) return null
  const merged = [...new Set([...a, ...b])]
  return merged.length > MAX_SIDEBAR_CONTAINERS ? null : merged
}

export interface ResourcesUpdatedBurst {
  /** Adds one event's containers to the burst, starting its window if this is the first. */
  push: (containers: SidebarContainers) => void
  /** Drops the pending burst without flushing it. */
  cancel: () => void
}

/**
 * Collects events into bursts: the first starts a window of `windowMs`, and when it closes `flush` gets
 * every container named in it (null if any event named none). The window doesn't restart per event, so a
 * steady stream still flushes every `windowMs`.
 */
export const createResourcesUpdatedBurst = (
  flush: (containers: SidebarContainers) => void,
  windowMs: number = RESOURCES_UPDATED_WINDOW_MS
): ResourcesUpdatedBurst => {
  let pending: SidebarContainers = []
  let timer: ReturnType<typeof setTimeout> | null = null
  return {
    push: (containers) => {
      pending = mergeContainers(timer === null ? [] : pending, containers)
      if (timer !== null) return
      timer = setTimeout(() => {
        timer = null
        flush(pending)
      }, windowMs)
    },
    cancel: () => {
      if (timer !== null) clearTimeout(timer)
      timer = null
    }
  }
}
